import { useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { compactNum, flagSrc, localName, type Country, type Lang } from '../lib/countries';
import { geoTerm } from '../lib/country';
import CountryLink from './CountryLink';

interface AnswerContentState {
  key: string;
  epithet: string | null;
  capital: string;
}

/**
 * Clickable learning card shown after an answer is revealed.
 * Editorial content and localized capitals stay lazy: they are fetched only after
 * the round ends, while the country identity and numeric facts render at once.
 */
export default function CountryAnswerCard({ country }: { country: Country }) {
  const { t, lang } = useI18n();
  const contentKey = `${country.cca3}:${lang}`;
  const [content, setContent] = useState<AnswerContentState>({ key: '', epithet: null, capital: '' });

  useEffect(() => {
    let active = true;
    const prosePromise = import('../data/load-country-content')
      .then(({ loadCountryContent }) => loadCountryContent(country.cca3, lang))
      .catch(() => null);
    const capitalPromise = import('../data/capitals-i18n.json')
      .then(({ default: capitals }) => {
        const names = capitals as Record<string, Partial<Record<Lang, string>>>;
        return names[country.cca3]?.[lang] ?? country.capital;
      })
      .catch(() => country.capital);
    void Promise.all([prosePromise, capitalPromise]).then(([prose, capital]) => {
      if (active) {
        setContent({ key: contentKey, epithet: prose?.epithet ?? null, capital });
      }
    });
    return () => {
      active = false;
    };
  }, [contentKey, country.capital, country.cca3, lang]);

  const name = localName(country, lang);
  const subregion = geoTerm(country.subregion, lang);
  const ready = content.key === contentKey;
  const epithet = ready ? content.epithet : null;
  const capital = ready ? content.capital : '';
  const hemisphere = country.hemisphere === 'N'
    ? t.answerCard.northernHemisphere
    : t.answerCard.southernHemisphere;
  const fallback = `${country.landlocked ? t.country.landlocked : t.country.coastal} · ${hemisphere}`;

  return (
    <CountryLink country={country} lang={lang} className="answer-card">
      <span className="answer-card-flag-wrap" aria-hidden="true">
        <img className="answer-card-flag" src={flagSrc(country)} alt="" />
      </span>

      <span className="answer-card-body">
        <span className="answer-card-kicker">{t.answerWas}</span>
        <span className="answer-card-title-row">
          <strong className="answer-card-title">{name}</strong>
          <span className="answer-card-cta" aria-hidden="true">
            <span>{t.answerCard.viewProfile}</span>
            <span className="answer-card-cta-arrow">→</span>
          </span>
        </span>
        <span
          className={`answer-card-location${ready ? '' : ' loading'}`}
          aria-hidden={!ready}
        >
          {[capital, subregion].filter(Boolean).join(' · ')}
        </span>
        <span
          className={`answer-card-copy${ready && !epithet ? ' fallback' : ''}${ready ? '' : ' loading'}`}
          aria-hidden={!ready}
        >
          {ready ? (epithet ?? fallback) : '\u00a0'}
        </span>

        <span className="answer-card-facts">
          <span className="answer-card-fact">
            <span>{t.country.f.population}</span>
            <strong>{compactNum(country.population, lang)}</strong>
          </span>
          <span className="answer-card-fact">
            <span>{t.country.f.area}</span>
            <strong>{compactNum(country.area, lang)} km²</strong>
          </span>
          <span className="answer-card-fact">
            <span>{t.cols.borders}</span>
            <strong>{country.borders.length.toLocaleString(lang)}</strong>
          </span>
        </span>
      </span>
    </CountryLink>
  );
}
